import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Grid,
  Switch,
  TextField,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import moment from "moment";
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { Link } from "react-router-dom";
import {
  BulkSendSettingsStatus,
  OrganizationBulkSendSettings,
  SendOption,
} from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";
import { routes } from "schemas/routes";

import SendTemplateOption from "components/BulkSendDialog/PickSendOption";
import TemplateScreen from "components/TemplateScreen";
import { Template } from "features/Influencer/ContactList/schema";
import { fetcherAuth } from "utils/api";
import { getLocation } from "utils/influencer";
import { trackEvent } from "utils/tracking";

import RunPitchScheduleDialog from "./RunPitchScheduleDialog";
import styles from "./styles";

const MAX_BRANDS_LIMIT = 400;
const DEFAULT_BRANDS = 100;
const DEFAULT_REPITCH_BRANDS = 0;

interface Props {
  open: boolean;
  handleClose: () => void;
  settings: OrganizationBulkSendSettings;
  setSettings: Dispatch<SetStateAction<OrganizationBulkSendSettings>>;
  selectedTemplates: Template[];
  setSelectedTemplates: Dispatch<SetStateAction<Template[]>>;
}

export default function UpcomingPitchScheduleDialog({
  open,
  handleClose,
  settings,
  setSettings,
  selectedTemplates,
  setSelectedTemplates,
}: Props) {
  const [loading, setLoading] = useState(false);
  const { getToken } = useAuth();
  const { setErrorAlert, setAlert } = useContext(AlertContext);
  const { currentOrg, profile } = useContext(OrganizationUserContext);
  const { outreachTemplates } = useContext(OutreachTemplatesContext);
  const [tempSettings, setTempSettings] =
    useState<OrganizationBulkSendSettings>(settings);
  const [validation, setValidation] = useState<string>("");
  const [validationMessage, setValidationMessage] = useState<
    string | JSX.Element
  >("");
  const [showRunDialog, setShowRunDialog] = useState<boolean>(false);
  const location = getLocation(profile);

  const pickOption = (option: SendOption) => {
    setTempSettings((prev) => ({
      ...prev,
      templateOption: option,
    }));
  };

  const handleTimezoneChange = (
    event: CustomEvent,
    newValue: string | null,
  ) => {
    if (newValue) {
      trackEvent("Timezone Changed", { timezone: newValue });
      setTempSettings((prev) => ({
        ...prev,
        timezone: newValue,
      }));
    }
  };

  const validatAllFields = () => {
    if (!tempSettings?.templateOption) {
      setValidation("templateOption");
      setValidationMessage("Please select a send option");
      return false;
    }

    if (
      tempSettings?.defaultNumberOfBrands === undefined ||
      tempSettings?.defaultNumberOfBrands === null ||
      tempSettings?.defaultNumberOfBrands < 0 ||
      tempSettings?.defaultNumberOfBrands > MAX_BRANDS_LIMIT
    ) {
      setValidation("numberOfBrands");
      setValidationMessage(
        `The number you select must be between 0 and ${MAX_BRANDS_LIMIT}.`,
      );
      return false;
    }

    if (
      tempSettings?.numberOfRepitchBrands === undefined ||
      tempSettings?.numberOfRepitchBrands === null ||
      tempSettings?.numberOfRepitchBrands < 0 ||
      tempSettings?.numberOfRepitchBrands > MAX_BRANDS_LIMIT
    ) {
      setValidation("numberOfRepitchBrands");
      setValidationMessage(
        `The number you select must be between 0 and ${MAX_BRANDS_LIMIT}.`,
      );
      return false;
    }

    if (!(selectedTemplates?.length > 0)) {
      setValidation("selectedTemplates");
      setValidationMessage(
        <>
          You do not have any templates. Please go to the{" "}
          <Link to={`/${routes.templates}`}>Templates</Link> tab to create one.
        </>,
      );
      return false;
    }
    setValidation("");
    setValidationMessage("");
    return true;
  };

  const createOrUpdateAutomation = async () => {
    const validate = validatAllFields();
    if (!validate) return;

    setLoading(true);

    try {
      const res = await fetcherAuth(
        getToken,
        tempSettings?.id
          ? `/api/organization/${currentOrg?.id}/bulk-send-settings/${tempSettings?.id}`
          : `/api/organization/${currentOrg?.id}/bulk-send-settings`,
        tempSettings?.id ? "PATCH" : "POST",
        {},
        {
          ...tempSettings,
          timezone: tempSettings?.timezone || moment.tz.guess(),
          templateIds: selectedTemplates?.map((t) => t.id),
        },
      );
      setTempSettings(res.bulkSendSetting);
      setSettings(res.bulkSendSetting);
      setAlert("Successfully updated settings", "success");
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedTemplates?.length === 0 && outreachTemplates?.length > 0) {
      const sortedByLastUsed = outreachTemplates?.sort((a, b) => {
        return (b.lastUsedAt || 0) - (a.lastUsedAt || 0);
      });
      if (sortedByLastUsed?.length > 0) {
        setSelectedTemplates([sortedByLastUsed[0]]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplates?.length, outreachTemplates?.length]);

  useEffect(() => {
    setTempSettings(settings);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={handleClose}>
      <DialogTitle>Set Up Automated Scheduling</DialogTitle>
      <DialogContent>
        <SendTemplateOption
          sendOption={tempSettings?.templateOption}
          pickOption={pickOption}
          groupingLoading={false}
          validOptions={[SendOption.same, SendOption.by_automation]}
        />

        {tempSettings?.templateOption && (
          <>
            <Box sx={{ mb: 3 }}>
              <strong>
                How many emails do you want to send per week to new brands?
              </strong>

              {validation === "numberOfBrands" ? (
                <FormHelperText sx={styles.error}>
                  <em>{validationMessage}</em>
                </FormHelperText>
              ) : (
                <FormHelperText>
                  Select a number between 0 and {MAX_BRANDS_LIMIT}. Emails will
                  be sent between Monday - Thursday.
                </FormHelperText>
              )}

              {tempSettings?.id && tempSettings?.id > 0 ? (
                <FormHelperText>
                  You currently have{" "}
                  <strong>{tempSettings?.additionalEmailCount || 0}</strong>{" "}
                  additional emails from email volumes package.
                  <br />
                  Your current total number of new brands sent per week will be{" "}
                  <strong>{tempSettings?.numberOfBrands}</strong> brands.
                </FormHelperText>
              ) : (
                <FormHelperText>
                  <em>
                    Create a settings to see the total email volume count{" "}
                  </em>
                </FormHelperText>
              )}

              <Grid item xs={12} sx={{ mt: 1 }}>
                <TextField
                  sx={{ width: 80 }}
                  size="small"
                  variant="outlined"
                  type="number"
                  placeholder={`${MAX_BRANDS_LIMIT}`}
                  value={tempSettings?.defaultNumberOfBrands || DEFAULT_BRANDS}
                  onChange={(e) => {
                    setTempSettings((prev) => ({
                      ...prev,
                      defaultNumberOfBrands: Math.floor(Number(e.target.value)),
                    }));
                    if (
                      Number(e.target.value) >= 0 &&
                      Number(e.target.value) <= MAX_BRANDS_LIMIT
                    ) {
                      setValidation("");
                      setValidationMessage("");
                    }
                  }}
                />
              </Grid>
            </Box>
            <Box sx={{ mb: 3 }}>
              <strong>
                How many re-pitching emails do you want to send per week?
              </strong>

              {validation === "numberOfRepitchBrands" ? (
                <FormHelperText sx={styles.error}>
                  <em>{validationMessage}</em>
                </FormHelperText>
              ) : (
                <FormHelperText>
                  Select a number between 0 and {MAX_BRANDS_LIMIT}. Emails will
                  be sent between Monday - Thursday to already pitched brands
                  that you have never gotten a response and you haven't sent any
                  emails to in the last 30 days.
                </FormHelperText>
              )}

              <Grid item xs={12} sx={{ mt: 1 }}>
                <TextField
                  sx={{ width: 80 }}
                  size="small"
                  variant="outlined"
                  type="number"
                  placeholder={`${MAX_BRANDS_LIMIT}`}
                  value={
                    tempSettings?.numberOfRepitchBrands ||
                    DEFAULT_REPITCH_BRANDS
                  }
                  onChange={(e) => {
                    setTempSettings((prev) => ({
                      ...prev,
                      numberOfRepitchBrands: Math.floor(Number(e.target.value)),
                    }));
                    if (
                      Number(e.target.value) >= 0 &&
                      Number(e.target.value) <= MAX_BRANDS_LIMIT
                    ) {
                      setValidation("");
                      setValidationMessage("");
                    }
                  }}
                />
              </Grid>
            </Box>
            {selectedTemplates?.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <strong>
                  Select the default templates to send for this option
                </strong>
                <FormHelperText>
                  Templates must contain only &#123;First Name&#125; and
                  &#123;Brand Name&#125; variables.
                  {tempSettings?.templateOption ===
                    SendOption.by_automation && (
                    <>
                      {" "}
                      <br />
                      If a brand does not belong to any automation, we will use
                      the templates below.
                    </>
                  )}
                </FormHelperText>
                <TemplateScreen
                  templates={selectedTemplates}
                  setTemplates={setSelectedTemplates}
                />
              </Box>
            )}

            {validation === "selectedTemplates" && (
              <FormHelperText sx={styles.error}>
                <em>{validationMessage}</em>
              </FormHelperText>
            )}
            <Box sx={{ mb: 3 }}>
              <strong>Select timezone</strong>
              <FormHelperText>
                Emails will be sent between 10:00 AM and 5:00 PM (using local
                timezone).{" "}
                {location && (
                  <>
                    <br />
                    Your location is <strong>{getLocation(profile)}</strong>.
                  </>
                )}
              </FormHelperText>

              <Autocomplete
                options={moment.tz.names()}
                value={tempSettings?.timezone || moment.tz.guess()}
                onChange={handleTimezoneChange}
                renderInput={(params) => (
                  <TextField {...params} sx={styles.timezonePicker} />
                )}
                disableClearable
                size="small"
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <strong>Personalize Outreach</strong>
              <Switch
                size="small"
                onChange={(e) => {
                  setTempSettings((prev) => ({
                    ...prev,
                    personalizeOutreach: e.target.checked,
                  }));
                }}
                checked={tempSettings?.personalizeOutreach}
              />
              {tempSettings?.personalizeOutreach ? (
                <FormHelperText>
                  If you want turn off the personalization feature, toggle the
                  switch above.
                </FormHelperText>
              ) : (
                <FormHelperText>
                  If you want to turn on the personalization feature, toggle the
                  switch above.
                </FormHelperText>
              )}
            </Box>

            <Box>
              <strong>Pause Outreach</strong>
              <Switch
                size="small"
                onChange={(e) => {
                  setTempSettings((prev) => ({
                    ...prev,
                    status: e.target.checked
                      ? BulkSendSettingsStatus.inactive
                      : BulkSendSettingsStatus.active,
                  }));
                }}
                checked={
                  tempSettings?.status === BulkSendSettingsStatus.inactive
                }
              />
              {tempSettings?.status === BulkSendSettingsStatus.inactive ? (
                <FormHelperText>
                  If you want to resume your outreach, toggle the switch above.
                </FormHelperText>
              ) : (
                <FormHelperText>
                  If you want to temporarily pause your outreach, toggle the
                  switch above.
                </FormHelperText>
              )}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          color="secondary"
          disableElevation
          disabled={!tempSettings?.templateOption}
          onClick={() => {
            setShowRunDialog(true);
          }}
        >
          Run Now
        </Button>
        <LoadingButton
          variant="contained"
          disableElevation
          disabled={!tempSettings?.templateOption}
          onClick={() => {
            createOrUpdateAutomation();
          }}
          loading={loading}
        >
          Confirm
        </LoadingButton>
      </DialogActions>

      {showRunDialog && (
        <RunPitchScheduleDialog
          open={showRunDialog}
          handleClose={() => setShowRunDialog(false)}
        />
      )}
    </Dialog>
  );
}
